<template>
  <avs-card class="pb-4">
    <!-- Overlay pendant génération de PDF -->
    <v-overlay
      :value="isGeneratingPdf"
      absolute
      style="top: 0; left: 0; right: 0; bottom: 0; z-index: 10"
    >
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      />
    </v-overlay>

    <template v-slot:title>
      <div
        class="pt-2 text-body-2 font-weight-regular"
        style="width: 100%; word-break: normal"
      >
        <!-- Cartes totaux -->
        <v-row
          class="mb-2"
          dense
        >
          <v-col
            cols="12"
            sm="4"
          >
            <v-card
              outlined
              class="pa-3"
              color="success"
              dark
            >
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-overline">{{ $t("Total Approuvées") }}</div>
                  <div class="text-h6 font-weight-bold">
                    {{ formatNumber(totals.approved) }} FCFA
                  </div>
                </div>
                <v-icon size="36">mdi-check-circle</v-icon>
              </div>
            </v-card>
          </v-col>
          <v-col
            cols="12"
            sm="4"
          >
            <v-card
              outlined
              class="pa-3"
              color="warning"
              dark
            >
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-overline">{{ $t("Total En attente") }}</div>
                  <div class="text-h6 font-weight-bold">
                    {{ formatNumber(totals.pending) }} FCFA
                  </div>
                </div>
                <v-icon size="36">mdi-clock-alert</v-icon>
              </div>
            </v-card>
          </v-col>
          <v-col
            cols="12"
            sm="4"
          >
            <v-card
              outlined
              class="pa-3"
              color="primary"
              dark
            >
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-overline">{{ $t("Total Dépenses") }}</div>
                  <div class="text-h6 font-weight-bold">{{ formatNumber(totals.all) }} FCFA</div>
                </div>
                <v-icon size="36">mdi-cash-multiple</v-icon>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Totaux par type -->
        <div
          v-if="totalsByType.length"
          class="d-flex flex-wrap mb-3"
          style="gap: 8px"
        >
          <v-chip
            v-for="bucket in totalsByType"
            :key="bucket.type"
            small
            label
            outlined
            :color="bucket.color"
          >
            <v-icon
              left
              small
              >{{ bucket.icon }}</v-icon
            >
            {{ $t(bucket.label) }} : {{ formatNumber(bucket.total) }} FCFA
          </v-chip>
        </div>

        <v-row class="align-center">
          <!-- Filtre par année -->
          <v-col
            cols="12"
            sm="2"
          >
            <v-text-field
              v-model="filters.year"
              :label="$t('Année')"
              type="number"
              dense
              outlined
              hide-details
              clearable
              @input="debouncedFetch"
              @click:clear="resetYearFilter"
            />
          </v-col>

          <!-- Filtre par mois -->
          <v-col
            cols="12"
            sm="3"
          >
            <v-autocomplete
              v-model="filters.month"
              :items="monthOptions"
              item-text="label"
              item-value="value"
              :label="$t('Mois')"
              dense
              outlined
              hide-details
              clearable
              @change="fetchFieldTypes"
              @click:clear="resetMonthFilter"
            />
          </v-col>

          <!-- Filtre par type -->
          <v-col
            cols="12"
            sm="2"
          >
            <v-autocomplete
              v-model="filters.type"
              :items="typeOptions"
              item-text="label"
              item-value="value"
              :label="$t('Type')"
              dense
              outlined
              hide-details
              clearable
              @change="fetchFieldTypes"
            />
          </v-col>

          <!-- Recherche -->
          <v-col
            cols="12"
            sm="3"
          >
            <v-text-field
              class="mr-2"
              clearable
              hide-details
              prepend-inner-icon="mdi-magnify"
              :label="$t('commons.search')"
              v-model="search"
            />
          </v-col>

          <!-- Bouton créer -->
          <v-col
            cols="12"
            sm="2"
            class="text-right"
          >
            <avs-createButton
              :spantext="
                $t('actions.create', {
                  entity: $t('Depense').toLowerCase()
                })
              "
              menu
              :name="
                $t('actions.new_female', {
                  entity: $t('Depense').toLowerCase()
                })
              "
              @click="handleOpenEditDialog()"
            />
          </v-col>
        </v-row>
      </div>
    </template>

    <template v-slot:content>
      <v-divider />
      <v-data-table
        class="ma-10"
        dense
        :headers="headers"
        :no-data-text="$t('messages.information.no_data')"
        :items="items"
        :items-per-page="-1"
        hide-default-footer
        :search="search"
        item-key="id"
        @dblclick:row="(e, { item }) => handleOpenEditDialog(item)"
      >
        <template v-slot:item.requested_amount="{ item }">
          <span class="font-weight-bold">{{ formatNumber(item.requested_amount) }} FCFA</span>
        </template>

        <template v-slot:item.expense_type="{ item }">
          <div class="d-flex align-center justify-center">
            <v-chip
              small
              label
              :color="typeMeta(item).color"
              dark
            >
              <v-icon
                left
                small
                >{{ typeMeta(item).icon }}</v-icon
              >
              {{ $t(typeMeta(item).label) }}
            </v-chip>
            <v-tooltip
              v-if="paymentMeta(item)"
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-icon
                  small
                  class="ml-2"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ paymentMeta(item).icon }}
                </v-icon>
              </template>
              <span>{{ $t(paymentMeta(item).label) }}</span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:item.expense_date="{ item }">
          {{ formatExpenseDate(item) }}
        </template>

        <template v-slot:item.status="{ item }">
          <component :is="handleStatusComponent(item)" />
        </template>

        <template v-slot:item.checked="{ item }">
          <div class="d-flex justify-center">
            <avs-button
              v-if="item.status != 'approved' && item.status != 'rejected'"
              icon
              color="secondary"
              :iconI="'mdi-checkbox-multiple-outline'"
              label
              fab
              small
              @click.stop="handleOpenConfirmDialog(item)"
              :spantext="
                $t('actions.open', {
                  entity: $t('commons.drawer.authorisation').toLowerCase()
                })
              "
            />
          </div>
        </template>

        <template v-slot:item.receipt="{ item }">
          <div class="d-flex justify-center">
            <!-- Reçu PDF pour dépenses Event -->
            <v-btn
              v-if="item.status === 'approved' && item.expense_type === 'Event'"
              icon
              small
              color="primary"
              @click="exportReceipt(item)"
              :loading="contractLoading[item.id]"
              :disabled="contractLoading[item.id]"
            >
              <v-icon>mdi-download</v-icon>
            </v-btn>
            <!-- Pièce jointe pour dépenses non-Event -->
            <v-btn
              v-else-if="item.expense_type !== 'Event' && item.attachment_url"
              icon
              small
              color="secondary"
              :href="item.attachment_url"
              target="_blank"
            >
              <v-icon>mdi-paperclip</v-icon>
            </v-btn>
          </div>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-center">
            <avs-editButton
              @click.stop="handleOpenEditDialog(item, false)"
              :spantext="
                $t('actions.edit', {
                  entity: $t('commons.drawer.authorisation').toLowerCase()
                })
              "
            />
            <avs-button
              icon
              color="secondary"
              :iconI="'mdi-eye'"
              label
              fab
              small
              @click.stop="handleOpenEditDialog(item)"
              :spantext="
                $t('actions.open', {
                  entity: $t('commons.drawer.authorisation').toLowerCase()
                })
              "
            />
            <avs-deleteButton
              @click.stop="handleOpenDeleteDialog(item)"
              :spantext="$t('actions.delete', { entity: $t('commons.fieldtype').toLowerCase() })"
            />
          </div>
        </template>
      </v-data-table>

      <!-- Dialog de confirmation (Accepter/Rejeter) -->
      <v-dialog
        v-model="confirmDialogIsOpen"
        width="40vw"
      >
        <v-card>
          <v-card-title class="headline d-flex justify-center">
            {{ $t("Authorisation") }}
          </v-card-title>
          <v-card-text class="text-center">
            <p>Voulez-vous autoriser la depense {{ selectedItem?.name }}</p>
            <v-icon
              large
              class="my-4"
              color="primary"
            >
              mdi-help-circle-outline
            </v-icon>
          </v-card-text>
          <v-card-actions class="d-flex justify-center">
            <v-btn
              color="success"
              @click="confirmAction('accept')"
            >
              <v-icon left>mdi-check</v-icon>
              {{ $t("Accepter") }}
            </v-btn>
            <v-btn
              color="error"
              @click="confirmAction('reject')"
            >
              <v-icon left>mdi-close</v-icon>
              {{ $t("Rejeter") }}
            </v-btn>
            <v-btn
              color="secondary"
              @click="confirmAction('close')"
            >
              <v-icon left>mdi-close-circle-outline</v-icon>
              {{ $t("Fermer") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog d'édition -->
      <v-dialog
        v-model="editDialogIsOpen"
        width="40vw"
      >
        <component
          v-model="selectedItem"
          :hasUniqueName="hasUniqueName"
          :is="editDialogContent"
          :isEditable="isEditable"
          @cancel="handleCancel"
          @error="handleError"
          @success="handleSuccess"
        />
      </v-dialog>

      <!-- Dialog de suppression -->
      <DeleteDialog
        v-model="deleteDialogIsOpen"
        @cancel="handleCancel"
        @delete="handleDelete"
        resource="Notification"
      />
    </template>
  </avs-card>
</template>

<script>
import { toDatetime } from "@/utils/datetime"
import { AuthorisationDetailService } from "@/services"
import { mkUnique } from "@/utils/validator"
import DeleteDialog from "@/components/generics/dialogs/DeleteDialog.vue"
import AuthorisationEditor from "@/components/ri3/authorisation/AuthorisationEditor.vue"
import { Approved, Canceled, Pending } from "@/components/generics/status"
import { EXPENSE_TYPES, expenseTypeMeta, paymentMethodMeta } from "@/utils/expenseTypes"
import _ from "lodash"

export default {
  name: "AuthorisationDetail",
  components: {
    DeleteDialog,
    AuthorisationEditor,
    Approved,
    Canceled,
    Pending
  },

  created() {
    this.initializeFilters()
    this.fetchFieldTypes()
    this.debouncedFetch = _.debounce(this.fetchFieldTypes, 500)
  },

  computed: {
    hasUniqueName: {
      get() {
        const names = this.items.filter(e => e.name !== this.selectedItem?.name).map(e => e.name)
        return mkUnique(names, "name")
      }
    },
    project() {
      return this.$store.state.app.project
    },
    totals() {
      const out = { approved: 0, pending: 0, rejected: 0, all: 0 }
      for (const item of this.items) {
        const amount = Number(item.requested_amount) || 0
        out.all += amount
        if (item.status === "approved") out.approved += amount
        else if (item.status === "pending") out.pending += amount
        else if (item.status === "rejected") out.rejected += amount
      }
      return out
    },
    totalsByType() {
      const map = {}
      for (const item of this.items) {
        const type = item.expense_type || "—"
        const amount = Number(item.requested_amount) || 0
        if (!map[type]) {
          const meta = expenseTypeMeta(type, null)
          map[type] = { type, total: 0, color: meta.color, icon: meta.icon, label: meta.label }
        }
        map[type].total += amount
      }
      return Object.values(map).sort((a, b) => b.total - a.total)
    }
  },

  data() {
    const currentDate = new Date()
    return {
      headers: [
        {
          text: this.$t("Titre"),
          value: "name",
          sortable: true
        },
        {
          text: this.$t("Description"),
          value: "justification",
          sortable: true,
          filterable: false
        },
        {
          text: this.$t("Type"),
          value: "expense_type",
          align: "center",
          sortable: true,
          filterable: false
        },
        {
          text: this.$t("Montant"),
          value: "requested_amount",
          align: "right",
          sortable: true,
          filterable: false
        },
        {
          text: this.$t("Date de dépense"),
          value: "expense_date",
          sortable: true,
          filterable: false
        },
        {
          text: this.$t("commons.status.name"),
          value: "status",
          align: "center",
          sortable: true,
          filterable: false
        },
        {
          text: "",
          value: "checked",
          align: "center",
          sortable: false,
          filterable: false
        },
        {
          text: this.$t("Reçu"),
          value: "receipt",
          align: "center",
          sortable: false,
          filterable: false
        },
        {
          text: this.$t("commons.action"),
          value: "actions",
          align: "center",
          sortable: false,
          filterable: false
        }
      ],
      filters: {
        month: currentDate.getMonth() + 1, // Mois en cours (1-12)
        year: currentDate.getFullYear(), // Année en cours
        type: null
      },
      typeOptions: EXPENSE_TYPES.map(t => ({ label: this.$t(t.label), value: t.value })),
      monthOptions: [
        { label: this.$t("Janvier"), value: 1 },
        { label: this.$t("Février"), value: 2 },
        { label: this.$t("Mars"), value: 3 },
        { label: this.$t("Avril"), value: 4 },
        { label: this.$t("Mai"), value: 5 },
        { label: this.$t("Juin"), value: 6 },
        { label: this.$t("Juillet"), value: 7 },
        { label: this.$t("Août"), value: 8 },
        { label: this.$t("Septembre"), value: 9 },
        { label: this.$t("Octobre"), value: 10 },
        { label: this.$t("Novembre"), value: 11 },
        { label: this.$t("Décembre"), value: 12 }
      ],
      isEditable: true,
      items: [],
      search: "",
      deleteDialogIsOpen: false,
      editDialogIsOpen: false,
      editDialogContent: null,
      selectedItem: undefined,
      isGeneratingPdf: false,
      contractLoading: {},
      confirmDialogIsOpen: false,
      debouncedFetch: null
    }
  },

  methods: {
    toDatetime,

    typeMeta(item) {
      return expenseTypeMeta(item.expense_type, item.salary_category)
    },
    paymentMeta(item) {
      return paymentMethodMeta(item.payment_method)
    },

    /**
     * Initialiser les filtres avec le mois et l'année en cours
     */
    initializeFilters() {
      const currentDate = new Date()
      this.filters = {
        month: currentDate.getMonth() + 1,
        year: currentDate.getFullYear(),
        type: null
      }
    },

    /**
     * Réinitialiser le filtre du mois
     */
    resetMonthFilter() {
      this.filters.month = null
      this.fetchFieldTypes()
    },

    /**
     * Réinitialiser le filtre de l'année à l'année en cours
     */
    resetYearFilter() {
      this.filters.year = new Date().getFullYear()
      this.fetchFieldTypes()
    },

    /**
     * Exporter le reçu PDF pour une dépense
     */
    async exportReceipt(item) {
      this.$set(this.contractLoading, item.id, true)
      this.isGeneratingPdf = true
      try {
        const blob = await AuthorisationDetailService.getReceiptUrl(this, item.id)
        const url = URL.createObjectURL(blob)
        window.open(url)
      } catch {
        this.$store.commit("app/message", {
          msg: this.$t("messages.error.payment_receipt_failed"),
          color: "error"
        })
      } finally {
        this.isGeneratingPdf = false
        this.$delete(this.contractLoading, item.id)
      }
    },

    /**
     * Récupérer la liste des dépenses avec filtres
     */
    fetchFieldTypes() {
      // Construire les paramètres de requête
      const params = {}
      if (this.filters.month) {
        params.month = this.filters.month
      }
      if (this.filters.year) {
        params.year = this.filters.year
      }
      if (this.filters.type) {
        params.type = this.filters.type
      }

      AuthorisationDetailService.getAll(this, this.project.id, params)
        .then(response => {
          this.items = response
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          }
        })
    },

    /**
     * Formater la date de dépense (fallback sur created_at pour rétro-compat)
     */
    formatExpenseDate(item) {
      const value = item.expense_date || item.created_at
      if (!value) return ""
      const d = new Date(value)
      if (isNaN(d.getTime())) return ""
      return d.toLocaleDateString("fr-FR")
    },

    /**
     * Formater les nombres avec séparateur de milliers (locale FR : espace)
     */
    formatNumber(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return "0"
      return new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(n)
    },

    /**
     * Ouvrir le dialog de confirmation
     */
    handleOpenConfirmDialog(item) {
      this.selectedItem = item
      this.confirmDialogIsOpen = true
    },

    /**
     * Gérer les actions du dialog de confirmation
     */
    confirmAction(action) {
      this.confirmDialogIsOpen = false
      switch (action) {
        case "accept":
          this.authorizeRequest(this.selectedItem)
          break
        case "reject":
          this.rejectRequest(this.selectedItem)
          break
        case "close":
          this.send(this.$t("messages.info.operation_canceled"), "info")
          break
        default:
          break
      }
    },

    /**
     * Autoriser une demande de dépense
     */
    authorizeRequest(item) {
      AuthorisationDetailService.authorize(item.id)
        .then(() => {
          this.send(this.$t("messages.success.approved"), "success")
          this.handleSuccess()
        })
        .catch(error => {
          const message = error?.response?.data?.state || this.$t("messages.error.operation_failed")
          this.handleError(message)
        })
    },

    /**
     * Rejeter une demande de dépense
     */
    rejectRequest(item) {
      AuthorisationDetailService.reject(item.id)
        .then(() => {
          this.send(this.$t("messages.success.rejected"), "success")
          this.handleSuccess()
        })
        .catch(error => {
          const message = error?.response?.data?.state || this.$t("messages.error.operation_failed")
          this.handleError(message)
        })
    },

    /**
     * Retourner le composant de statut approprié
     */
    handleStatusComponent(action) {
      return action.status
    },

    /**
     * Ouvrir le dialog de suppression
     */
    handleOpenDeleteDialog(item) {
      this.selectedItem = item
      this.deleteDialogIsOpen = true
    },

    /**
     * Ouvrir le dialog d'édition
     */
    handleOpenEditDialog(item, show = true) {
      this.selectedItem = undefined
      this.isEditable = true
      if (item) {
        this.selectedItem = item
        if (show) {
          this.isEditable = false
        }
      }
      this.handleOpenEditDialogByComponent()
    },

    /**
     * Ouvrir le dialog d'édition avec le bon composant
     */
    handleOpenEditDialogByComponent() {
      this.editDialogIsOpen = true
      this.editDialogContent = "AuthorisationEditor"
    },

    /**
     * Annuler et fermer les dialogs
     */
    handleCancel() {
      this.selectedItem = undefined
      this.deleteDialogIsOpen = false
      this.editDialogIsOpen = false
      this.editDialogContent = null
    },

    /**
     * Gérer les erreurs
     */
    handleError(message) {
      this.send(message || "Enter a valid email address.", "error")
    },

    /**
     * Gérer le succès des opérations
     */
    handleSuccess() {
      this.handleCancel()
      this.$nextTick(() => {
        this.fetchFieldTypes()
        this.send(this.$t("messages.success.operation"), "success")
      })
    },

    /**
     * Supprimer une dépense
     */
    handleDelete() {
      AuthorisationDetailService.delete(this.selectedItem.id)
        .then(() => {
          this.handleSuccess()
        })
        .catch(error => {
          const message = error?.response.data?.state
          this.handleError(message)
        })
    },

    /**
     * Envoyer un message à l'utilisateur
     */
    send(msg, color) {
      this.$store.commit("app/message", { msg, color })
    }
  }
}
</script>

<style scoped>
.custom-toolbar {
  background-color: transparent !important;
}
</style>
